/**
 * 営業管理API サービス
 *
 * 営業案件、KPI、マスタ管理、エクスポート等のAPI呼び出しを提供
 *
 * @module services/salesApi
 * @created 2025-11-16
 */

import axios from 'axios';

// Axios インスタンス作成
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000'), // 30秒（エクスポート処理を考慮）
});

// リクエストインターセプター
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// レスポンスインターセプター
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      if (
        error.response?.data?.message?.includes('token') ||
        error.response?.data?.message?.includes('expired') ||
        error.response?.data?.message?.includes('Invalid')
      ) {
        console.warn('トークンが無効または期限切れです。ログインページにリダイレクトします。');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.clear();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ===============================
// 型定義
// ===============================

export interface SalesCase {
  case_id: number;
  case_no: string;
  case_name: string;
  case_summary?: string;
  account_id: number;
  account_name?: string;
  service_department?: string;
  end_user?: string;
  case_source?: string;
  business_category?: string;
  assigned_employee_id: string;
  assigned_employee_name?: string;
  sales_phase_id: number;
  sales_phase_name?: string;
  sales_phase_code?: string;
  phase_color_code?: string;
  initial_registration_reason?: string;
  expected_order_amount?: number;
  expected_order_date?: string;
  fiscal_year_planned_sales?: number;
  total_planned_sales?: number;
  fiscal_year_expected_sales?: number;
  current_year_planned_sales?: number;
  final_sales?: number;
  current_year_final_sales?: number;
  gross_profit?: number;
  progress_sales?: number;
  progress_rate?: number;
  status: string;
  is_order_confirmed?: boolean;
  external_project_id?: string;
  external_sync_at?: string;
  next_action_date?: string;
  next_action_memo?: string;
  phase_changed_at?: string;
  days_in_phase?: number;
  close_result?: string;
  close_reason?: string;
  close_reason_codes?: string[];
  phase_goal?: string;
  required_actions?: Array<{ action: string; completed: boolean }>;
  suggested_next_actions?: string[];
  target_days?: number;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
  created_by_name?: string;
  updated_by_name?: string;
  deleted_at?: string;
  // 承認ワークフロー関連
  approval_status?: string;
  pending_data?: PendingData;
  approver_id?: string;
  approval_requested_at?: string;
  approval_comment?: string;
  has_pending_preview?: boolean;
  approved_values?: {
    case_name?: string;
    case_summary?: string;
    sales_phase_name?: string;
    sales_phase_id?: number;
    assigned_employee_name?: string;
    assigned_employee_id?: string;
    business_category?: string;
    next_action_date?: string;
    next_action_memo?: string;
    status?: string;
    expected_order_date?: string;
    external_project_id?: string;
    close_result?: string;
    service_department?: string;
    end_user?: string;
    account_name?: string;
    expected_order_amount?: number;
    gross_profit?: number;
    final_sales?: number;
    current_year_planned_sales?: number;
    current_year_final_sales?: number;
  };
  // 今週更新フラグ
  updated_this_week?: boolean;
}

export interface SalesPhase {
  phase_id: number;
  phase_name: string;
  phase_code: string;
  display_order: number;
  individual_conversion_rate?: number;
  cumulative_conversion_rate?: number;
  color_code?: string;
  description?: string;
  is_active: boolean;
  phase_goal?: string;
  required_actions?: Array<{ action: string; completed: boolean }>;
  suggested_next_actions?: string[];
  target_days?: number;
}

export interface CloseReason {
  reason_code: string;
  reason_name: string;
  category: string;
  display_order: number;
}

export interface Account {
  account_id: number;
  account_name: string;
  account_name_kana?: string;
  industry?: string;
  notes?: string;
  is_active: boolean;
  created_at?: string;
  created_by?: string;
}

export interface Vendor {
  vendor_id: number;
  vendor_name: string;
  vendor_name_kana?: string;
  industry?: string;
  notes?: string;
  is_active: boolean;
  created_at?: string;
  created_by?: string;
}

/**
 * 要員配置データ（案件・予算共通）
 */
export interface ResourceAllocation {
  allocation_id?: number;
  resource_name: string;
  resource_type: 'employee' | 'internal_pool' | 'contractor' | 'outsource_pool' | 'undetermined';
  employee_id?: string;
  vendor_id?: number | null;
  unit_price: number;
  cost_rate: number;
  start_month: string;
  end_month: string;
  monthly?: Array<{
    month: number;
    planned_hours?: number;
    actual_hours?: number;
  }>;
}

export interface KpiDashboardData {
  summary: {
    total_cases: number;
    total_expected_amount: number;        // パイプライン総額（換算なし）
    weighted_expected_amount: number;     // 現況パイプライン期待値（status=active・フェーズ換算後。年度達成率の実績とは別定義）
    forecast_amount: number;              // フォーキャスト総額（換算なし）
    total_current_year_planned_sales: number;
    total_final_sales: number;
    total_gross_profit: number;
  };
  by_phase: Array<{
    phase_id: number;
    phase_name: string;
    case_count: number;
    total_expected_amount: number;
  }>;
  by_month: Array<{
    month: string;
    case_count: number;
    total_expected_amount: number;
  }>;
  top_accounts: Array<{
    account_id: number;
    account_name: string;
    case_count: number;
    total_expected_amount: number;
  }>;
}

export interface SalesCasePhaseHistory {
  history_id: number;
  sales_phase_name?: string;
  change_reason?: string;
  expected_order_amount?: number;
  expected_order_date?: string;
  current_year_planned_sales?: number;
  final_sales?: number;
  gross_profit?: number;
  changed_by: string;
  changed_by_name?: string;
  changed_at: string;
  change_type?: string;
  approver_id?: string;
  approver_name?: string;
  approval_comment?: string;
}

export interface ExternalSyncHistory {
  sync_id: number;
  case_id: number;
  case_no?: string;
  external_project_id: string;
  sync_type: string;
  sync_status: string;
  sync_started_at: string;
  sync_completed_at?: string;
  error_message?: string;
  created_by_name?: string;
}

export interface SalesPhaseContribution {
  contribution_id: number;
  phase_name: string;
  phase_code: string;
  display_order: number;
  individual_conversion_rate: number;
  expected_order_amount_at_time: number;
  expected_contribution: number;
  is_actual_phase: boolean;
  phase_type_label: string;
  created_at: string;
}

export interface PipelinePhase {
  phase_id: number;
  phase_name: string;
  phase_code: string;
  display_order: number;
  color_code?: string;
  individual_conversion_rate?: number;
  case_count: number;
  total_expected_amount: number;
  total_current_year_sales?: number;
}

export interface PipelineData {
  phases: PipelinePhase[];
  summary: {
    total_active_cases: number;
    overall_conversion_rate: string;
  };
}

export interface StagnationPhase {
  phase_id: number;
  phase_name: string;
  phase_code: string;
  target_days: number;
  total_cases: number;
  avg_days: number;
  warning_cases: number;
  is_over_target: boolean;
}

export interface LostReasonItem {
  reason_code: string;
  reason_name: string;
  category: string;
  count: number;
  percentage?: string;
}

export interface LostReasonData {
  by_reason: LostReasonItem[];
  by_category: Array<{
    category: string;
    count: number;
    percentage?: string;
  }>;
  by_phase?: Array<{
    phase_id: number | string;
    phase_name: string;
    phase_code?: string;
    lost_count: number;
    percentage?: string;
  }>;
  total_lost: number;
}

// 顧客別統計
export interface CaseMonthlyStats {
  month: string;
  man_months: number;
  confirmed_sales: number;
  expected_sales: number;
  confirmed_outsource_cost: number;
  expected_outsource_cost: number;
  confirmed_profit: number;
  expected_profit: number;
}

export interface AccountCaseStats {
  case_id: number;
  case_no: string;
  case_name: string;
  phase_name: string | null;
  start_month: string | null;
  end_month: string | null;
  man_months: number;
  confirmed_sales: number;
  expected_sales: number;
  confirmed_outsource_cost: number;
  expected_outsource_cost: number;
  confirmed_profit: number;
  expected_profit: number;
  monthly: CaseMonthlyStats[];
}

export interface AccountStatistics {
  account_id: number;
  account_name: string;
  case_count: number;
  total_man_months: number;
  confirmed_sales: number;
  expected_sales: number;
  confirmed_outsource_cost: number;
  expected_outsource_cost: number;
  confirmed_profit: number;
  expected_profit: number;
  cases: AccountCaseStats[];
}

export interface AccountStatisticsData {
  fiscal_year: number;
  accounts: AccountStatistics[];
}

export interface YearlySummaryData {
  won_count: number;
  won_amount: number;
  lost_count: number;
  lost_amount: number;
  total_expected_amount: number;       // フェーズ換算後の期待売上額
  total_expected_raw_amount: number;   // 換算前の期待売上額
  total_expected_count: number;
  year: number;
}

export interface BusinessCategoryHeatmapItem {
  category: string;
  case_count: number;
  total_amount: number;
}

export interface PhaseTransition {
  source: string;
  target: string;
  value: number;
  amount: number;
  transition_type: 'stayed' | 'progressed' | 'regressed' | 'won' | 'lost' | 'suspended' | 'deleted';
}

export interface PhaseTransitionData {
  base_month: string;
  compare_month: string;
  transitions: PhaseTransition[];
}

// KPI予実比較用型定義
export interface BudgetActualSummary {
  fiscal_year: number;
  summary: {
    target_sales: number;
    target_cost: number;
    target_profit: number;
    actual_sales: number;
    actual_cost: number;
    actual_profit: number;
    sales_achievement_rate: number;
    cost_achievement_rate: number;
    profit_achievement_rate: number;
    target_count: number;
  };
}

export interface BudgetTrendItem {
  month: string;
  month_num: number;
  target_sales: number;
  target_cost: number;
  target_profit: number;
  actual_sales: number;
  actual_cost: number;
  actual_profit: number;
  cumulative_target_sales: number;
  cumulative_actual_sales: number;
  cumulative_target_profit: number;
  cumulative_actual_profit: number;
  achievement_rate: number;
}

export interface BudgetByDepartmentItem {
  department_id: string;
  department_name: string;
  target_sales: number;
  target_cost: number;
  target_profit: number;
  actual_sales: number;
  actual_cost: number;
  actual_profit: number;
  target_count: number;
  sales_achievement_rate: number;
  profit_achievement_rate: number;
}

export interface BudgetByEmployeeItem {
  employee_id: string;
  employee_name: string;
  department_id: string;
  department_name: string;
  target_sales: number;
  target_cost: number;
  target_profit: number;
  actual_sales: number;
  actual_cost: number;
  actual_profit: number;
  target_count: number;
  sales_achievement_rate: number;
  profit_achievement_rate: number;
}

// 金額変更アラート型定義
export interface AmountChangeAlert {
  case_id: number;
  case_no: string;
  case_name: string;
  account_name: string;
  assigned_employee_name: string;
  old_amount: number;
  new_amount: number;
  diff_amount: number;
  diff_percent: number;
  old_gross_profit: number;
  new_gross_profit: number;
  changed_at: string;
}

export interface AmountChangeAlertsResponse {
  period_days: number;
  summary: {
    total_cases: number;
    increase_count: number;
    decrease_count: number;
    net_change: number;
  };
  alerts: AmountChangeAlert[];
}

// ===============================
// 営業担当者予実管理（評価制度規程準拠）
// ===============================

export interface EmployeeTarget {
  target_id: number;
  fiscal_year: number;
  employee_id: string;
  employee_name: string;
  department_name: string | null;
  target_expected_sales: number;
  target_gross_profit: number;
  notes: string | null;
  version: number;
  is_current: boolean;
  created_at: string;
  updated_at: string | null;
}

export interface PhaseContributionDetail {
  employee_id: string;
  case_id: number;
  case_no: string;
  case_name: string;
  phase_name: string;
  expected_order_amount: number;
  contribution_rate: number;
  contribution_amount: number;
  changed_at: string;
  change_type: string;
}

export interface PhaseContributionSummary {
  employee_id: string;
  employee_name: string;
  department_name: string | null;
  case_count: number;
  phase_change_count: number;
  total_contribution: number;
}

export interface PhaseContributionsResponse {
  fiscal_year: number;
  summary: PhaseContributionSummary[];
  details: PhaseContributionDetail[];
}

export interface EmployeePerformanceItem {
  employee_id: string;
  employee_name: string;
  department_name: string | null;
  target_expected_sales: number;
  actual_expected_sales: number;
  expected_sales_achievement_rate: number;
  target_gross_profit: number;
  actual_gross_profit: number;
  gross_profit_achievement_rate: number;
}

export interface EmployeePerformanceResponse {
  fiscal_year: number;
  summary: {
    total_target_expected_sales: number;
    total_actual_expected_sales: number;
    expected_sales_achievement_rate: number;
    total_target_gross_profit: number;
    total_actual_gross_profit: number;
    gross_profit_achievement_rate: number;
  };
  employees: EmployeePerformanceItem[];
}

export interface PerformanceTrendItem {
  period: string;
  period_contribution: number;
  cumulative_contribution: number;
  achievement_rate: number;
}

export interface EmployeeTrendData {
  employee_id: string;
  employee_name: string;
  target_expected_sales: number;
  has_target?: boolean;
  trend: PerformanceTrendItem[];
}

export interface PerformanceTrendResponse {
  fiscal_year: number;
  period_type: string;
  period_label: string;
  periods: string[];
  employees: EmployeeTrendData[];
}

// ===============================
// 営業案件管理API
// ===============================

export interface SalesCaseSearchResult {
  case_id: number;
  case_no: string;
  case_name: string;
  account_id: number;
  account_name: string;
  sales_phase_id: number;
  sales_phase_name: string;
  service_department?: string;
  business_category?: string;
  assigned_employee_id: string;
  assigned_employee_name: string;
  status: string;
}

export interface SalesAssignee {
  employee_id: string;
  name: string;
}

export const salesCasesApi = {
  /**
   * 案件検索（Autocomplete用）
   */
  searchCases: async (query: string, limit?: number): Promise<SalesCaseSearchResult[]> => {
    const response = await api.get<{
      success: boolean;
      data: {
        cases: SalesCaseSearchResult[];
        hasMore: boolean;
      };
    }>('/sales/cases/search', { params: { q: query, limit: limit || 50 } });
    return response.data.data.cases;
  },

  /**
   * 案件担当者検索（Autocomplete用）
   * 実際に案件担当になっている社員のみを取得
   */
  searchAssignees: async (query: string, limit?: number): Promise<SalesAssignee[]> => {
    const response = await api.get<{
      success: boolean;
      data: SalesAssignee[];
    }>('/sales/cases/assignees', { params: { q: query, limit: limit || 50 } });
    return response.data.data;
  },

  /**
   * 案件一覧取得
   */
  getCases: (params?: {
    search?: string;
    account_name?: string;
    account_id?: number;
    assigned_employee_id?: string;
    employee_id?: string;
    department_id?: number;
    sales_phase_id?: number;
    sales_phase_ids?: string;
    phase_id?: number;
    status?: string;
    close_result?: string;
    approval_status?: string;
    business_category?: string;
    expected_order_date_from?: string;
    expected_order_date_to?: string;
    expected_order_amount_min?: number;
    expected_order_amount_max?: number;
    days_in_phase_min?: number;
    stagnant?: boolean;
    next_action_overdue?: boolean;
    updated_this_week?: boolean;
    fiscal_year?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
    page?: number;
    limit?: number;
    offset?: number;
  }) =>
    api.get<{
      success: boolean;
      data: {
        cases: SalesCase[];
        pagination: {
          current_page: number;
          total_pages: number;
          total_count: number;
          per_page: number;
        };
      };
    }>('/sales/cases', { params }),

  /**
   * 案件詳細取得
   */
  getCaseById: (caseId: number) =>
    api.get<{ success: boolean; data: SalesCase }>(`/sales/cases/${caseId}`),

  /**
   * 案件新規登録
   */
  createCase: (data: {
    case_name: string;
    case_summary: string;
    account_id: number;
    service_department?: string;
    end_user?: string;
    case_source?: string;
    assigned_employee_id?: string;
    sales_phase_id: number;
    initial_registration_reason?: string;
    expected_order_date?: string;
    business_category?: string;
    status?: string;
    external_project_id?: string;
    next_action_date?: string;
    next_action_memo?: string;
  }) =>
    api.post<{ success: boolean; data: { case_id: number } }>('/sales/cases', data),

  /**
   * 案件更新
   */
  updateCase: (caseId: number, data: Partial<SalesCase> & { change_reason?: string }) =>
    api.put<{ success: boolean; data: { message: string } }>(`/sales/cases/${caseId}`, data),

  /**
   * 案件削除
   */
  deleteCase: (caseId: number) =>
    api.delete<{ success: boolean; data: { message: string } }>(`/sales/cases/${caseId}`),

  /**
   * 案件フェーズ変更
   */
  changeCasePhase: (caseId: number, data: {
    new_phase_id: number;
    phase_change_reason?: string;
  }) =>
    api.post<{ success: boolean; data: { message: string } }>(`/sales/cases/${caseId}/phase`, data),

  /**
   * 案件フェーズ履歴取得
   */
  getCasePhaseHistory: (caseId: number) =>
    api.get<{ success: boolean; data: { case_id: number; case_no: string; case_name: string; history: SalesCasePhaseHistory[] } }>(`/sales/cases/${caseId}/history`),

  /**
   * 外部案件IDマッピング
   */
  mapExternalProjectId: (data: {
    case_id: number;
    external_project_id: string;
  }) =>
    api.post<{ success: boolean; data: { message: string } }>('/sales/cases/map-external-project-id', data),

  /**
   * フェーズ別期待受注額取得
   */
  getPhaseContributions: (caseId: number) =>
    api.get<{
      success: boolean;
      data: {
        case_id: number;
        case_no: string;
        case_name: string;
        current_phase: string;
        total_contribution: number;
        phase_contributions: SalesPhaseContribution[];
      };
    }>(`/sales/cases/${caseId}/phase-contributions`),

  /**
   * 案件行動履歴一覧取得
   */
  getCaseActivities: (caseId: number) =>
    api.get<{
      success: boolean;
      data: Array<{
        activity_id: number;
        case_id: number;
        activity_date: string;
        activity_type: string;
        description?: string;
        created_by: string;
        created_by_name?: string;
        created_at: string;
        updated_at: string;
      }>;
    }>(`/sales/cases/${caseId}/activities`),

  /**
   * 案件行動履歴追加
   */
  addCaseActivity: (caseId: number, data: {
    activity_date: string;
    activity_type: string;
    description?: string;
  }) =>
    api.post<{
      success: boolean;
      data: { activity_id: number; message: string };
    }>(`/sales/cases/${caseId}/activities`, data),

  /**
   * 案件行動履歴更新
   */
  updateCaseActivity: (caseId: number, activityId: number, data: {
    activity_date: string;
    activity_type: string;
    description?: string;
  }) =>
    api.put<{
      success: boolean;
      data: { message: string };
    }>(`/sales/cases/${caseId}/activities/${activityId}`, data),

  /**
   * 案件行動履歴削除
   */
  deleteCaseActivity: (caseId: number, activityId: number) =>
    api.delete<{
      success: boolean;
      data: { message: string };
    }>(`/sales/cases/${caseId}/activities/${activityId}`),

  /**
   * 案件要員配置一覧取得
   */
  getCaseAllocations: (caseId: number) =>
    api.get<{
      success: boolean;
      data: CaseResourceAllocation[];
      has_pending_preview?: boolean;
      approved_allocations?: CaseResourceAllocation[];
    }>(`/sales/cases/${caseId}/allocations`),

  /**
   * 案件要員配置一括保存
   */
  saveCaseAllocations: (caseId: number, allocations: Partial<CaseResourceAllocation>[]) =>
    api.post<{
      success: boolean;
      message: string;
    }>(`/sales/cases/${caseId}/allocations`, { allocations }),
};

// 案件要員配置型
export interface CaseResourceAllocation {
  allocation_id?: number;
  case_id?: number;
  employee_id?: string;
  resource_name: string;
  resource_type: 'employee' | 'internal_pool' | 'contractor' | 'outsource_pool' | 'undetermined';
  vendor_id?: number | null;
  vendor_name?: string;
  unit_price: number;
  cost_rate: number;
  start_month: string;
  end_month: string;
  is_active?: boolean;
  employee_name?: string;
}

// ===============================
// KPI・レポートAPI
// ===============================

export const salesKpiApi = {
  /**
   * KPIダッシュボード取得
   */
  getKpiDashboard: (params?: {
    department_id?: number;
    employee_id?: string;
  }) =>
    api.get<{ success: boolean; data: KpiDashboardData }>('/sales/kpi/dashboard', { params }),

  /**
   * フェーズ別KPI取得
   */
  getKpiByPhase: (params?: {
    department_id?: number;
    employee_id?: string;
  }) =>
    api.get<{
      success: boolean;
      data: Array<{
        phase_id: number;
        phase_name: string;
        case_count: number;
        total_expected_amount: number;
        total_current_year_planned_sales: number;
      }>;
    }>('/sales/kpi/by-phase', { params }),

  /**
   * 担当者別KPI取得
   */
  getKpiByEmployee: (params?: {
    department_id?: number;
  }) =>
    api.get<{
      success: boolean;
      data: Array<{
        employee_id: string;
        employee_name: string;
        department_name: string;
        case_count: number;
        total_expected_amount: number;
        total_current_year_planned_sales: number;
        total_gross_profit: number;
      }>;
    }>('/sales/kpi/by-employee', { params }),

  /**
   * 部署別KPI取得
   */
  getKpiByDepartment: () =>
    api.get<{
      success: boolean;
      data: Array<{
        department_id: number;
        department_name: string;
        case_count: number;
        total_expected_amount: number;
        total_current_year_planned_sales: number;
        total_gross_profit: number;
      }>;
    }>('/sales/kpi/by-department'),

  /**
   * 期待売上取得（規程第5条準拠）
   */
  getExpectedSales: (params?: {
    department_id?: number;
    employee_id?: string;
  }) =>
    api.get<{
      success: boolean;
      data: {
        total_expected_sales: number;
        total_case_count: number;
        total_phase_contribution_count: number;
        by_phase: Array<{
          phase_id: number;
          phase_name: string;
          contribution_count: number;
          total_expected_contribution: number;
        }>;
      };
    }>('/sales/kpi/expected-sales', { params }),

  /**
   * パイプライン分析取得
   */
  getPipeline: (params?: { employee_id?: string }) =>
    api.get<{
      success: boolean;
      data: {
        phases: Array<{
          phase_id: number;
          phase_name: string;
          phase_code: string;
          display_order: number;
          color_code: string;
          individual_conversion_rate: number;
          case_count: number;
          total_expected_amount: number;
          total_current_year_sales: number;
        }>;
        summary: {
          total_active_cases: number;
          overall_conversion_rate: string;
        };
      };
    }>('/sales/kpi/pipeline', { params }),

  /**
   * 滞留分析取得
   */
  getStagnation: (params?: { employee_id?: string }) =>
    api.get<{
      success: boolean;
      data: Array<{
        phase_id: number;
        phase_name: string;
        phase_code: string;
        target_days: number;
        total_cases: number;
        avg_days: number;
        warning_cases: number;
        is_over_target: boolean;
      }>;
    }>('/sales/kpi/stagnation', { params }),

  /**
   * 失注理由分析取得
   */
  getLostReasons: (params?: { year?: number; employee_id?: string }) =>
    api.get<{
      success: boolean;
      data: {
        by_reason: Array<{
          reason_code: string;
          reason_name: string;
          category: string;
          count: number;
          percentage: string;
        }>;
        by_category: Array<{
          category: string;
          count: number;
          percentage: string;
        }>;
        by_phase: Array<{
          phase_id: number;
          phase_name: string;
          phase_code: string;
          lost_count: number;
          percentage: string;
        }>;
        total_lost: number;
        year: number | null;
      };
    }>('/sales/kpi/lost-reasons', { params }),

  /**
   * 年度別受注/失注サマリー取得
   */
  getYearlySummary: (params?: { year?: number; employee_id?: string }) =>
    api.get<{
      success: boolean;
      data: YearlySummaryData;
    }>('/sales/kpi/yearly-summary', { params }),

  /**
   * 事業カテゴリ別ヒートマップデータ取得
   */
  getBusinessCategoryHeatmap: (params?: { employee_id?: string }) =>
    api.get<{
      success: boolean;
      data: BusinessCategoryHeatmapItem[];
    }>('/sales/kpi/business-category-heatmap', { params }),

  /**
   * 顧客別統計データ取得
   */
  getAccountStatistics: (params?: { fiscal_year?: number; employee_id?: string }) =>
    api.get<{
      success: boolean;
      data: AccountStatisticsData;
    }>('/sales/kpi/account-statistics', { params }),

  /**
   * フェーズ遷移分析データ取得（サンキーダイアグラム用）
   */
  getPhaseTransitions: (params?: { base_date?: string; employee_id?: string }) =>
    api.get<{
      success: boolean;
      data: PhaseTransitionData;
    }>('/sales/kpi/phase-transitions', { params }),

  /**
   * フェーズ遷移詳細（案件一覧）取得
   */
  getPhaseTransitionCases: (params: {
    base_date: string;
    source: string;
    target: string;
    transition_type?: string;
    employee_id?: string;
  }) =>
    api.get<{
      success: boolean;
      data: {
        source: string;
        target: string;
        transition_type: string;
        cases: Array<{
          case_id: number;
          case_no: string;
          case_name: string;
          base_amount: number;
          current_amount: number;
          assigned_employee_name: string | null;
          account_name: string | null;
          transition_type: string;
        }>;
      };
    }>('/sales/kpi/phase-transition-cases', { params }),

  /**
   * 予実比較サマリー取得
   */
  getBudgetActual: (params?: {
    fiscal_year?: number;
    department_id?: string;
    case_category?: string;
    phase_filter?: string;
  }) =>
    api.get<{
      success: boolean;
      data: BudgetActualSummary;
    }>('/sales/kpi/budget-actual', { params }),

  /**
   * 予実トレンドデータ取得（グラフ用）
   */
  getBudgetTrend: (params?: {
    fiscal_year?: number;
    department_id?: string;
    case_category?: string;
  }) =>
    api.get<{
      success: boolean;
      data: {
        fiscal_year: number;
        trend: BudgetTrendItem[];
      };
    }>('/sales/kpi/budget-trend', { params }),

  /**
   * 部署別予実比較取得
   */
  getBudgetByDepartment: (params?: {
    fiscal_year?: number;
    case_category?: string;
  }) =>
    api.get<{
      success: boolean;
      data: {
        fiscal_year: number;
        departments: BudgetByDepartmentItem[];
      };
    }>('/sales/kpi/budget-by-department', { params }),

  /**
   * 担当者別予実比較取得
   */
  getBudgetByEmployee: (params?: {
    fiscal_year?: number;
    department_id?: string;
    case_category?: string;
  }) =>
    api.get<{
      success: boolean;
      data: {
        fiscal_year: number;
        employees: BudgetByEmployeeItem[];
      };
    }>('/sales/kpi/budget-by-employee', { params }),

  /**
   * 金額変更アラート取得
   * 直近の期間で金額変更があった案件を取得
   */
  getAmountChangeAlerts: (params?: {
    days?: number;
    limit?: number;
  }) =>
    api.get<{
      success: boolean;
      data: AmountChangeAlertsResponse;
    }>('/sales/kpi/amount-change-alerts', { params }),

  // ===============================
  // 営業担当者予実管理API（評価制度規程準拠）
  // ===============================

  /**
   * 担当者目標一覧取得
   */
  getEmployeeTargets: (fiscalYear?: number) =>
    api.get<{
      success: boolean;
      data: {
        fiscal_year: number;
        targets: EmployeeTarget[];
      };
    }>('/sales/kpi/employee-targets', { params: fiscalYear ? { fiscal_year: fiscalYear } : {} }),

  /**
   * 担当者目標設定・更新
   */
  setEmployeeTarget: (data: {
    fiscal_year: number;
    employee_id: string;
    target_expected_sales: number;
    target_gross_profit: number;
    notes?: string;
  }) =>
    api.post<{
      success: boolean;
      message: string;
      data: { target_id: number; version: number };
    }>('/sales/kpi/employee-targets', data),

  /**
   * フェーズ貢献実績取得
   */
  getPhaseContributions: (params?: {
    fiscal_year?: number;
    employee_id?: string;
  }) =>
    api.get<{
      success: boolean;
      data: PhaseContributionsResponse;
    }>('/sales/kpi/phase-contributions', { params }),

  /**
   * 担当者別予実サマリー取得
   */
  getEmployeePerformance: (fiscalYear?: number) =>
    api.get<{
      success: boolean;
      data: EmployeePerformanceResponse;
    }>('/sales/kpi/employee-performance', { params: fiscalYear ? { fiscal_year: fiscalYear } : {} }),

  /**
   * 推移データ取得
   */
  getPerformanceTrend: (params?: {
    fiscal_year?: number;
    period?: 'week' | 'month' | 'quarter';
    employee_id?: string;
  }) =>
    api.get<{
      success: boolean;
      data: PerformanceTrendResponse;
    }>('/sales/kpi/performance-trend', { params }),
};

// ===============================
// マスタ管理API
// ===============================

export interface AccountSearchResult {
  account_id: number;
  account_name: string;
  account_name_kana?: string;
  industry?: string;
}

export const salesMasterApi = {
  /**
   * 営業フェーズ一覧取得
   */
  getPhases: () =>
    api.get<{ success: boolean; data: SalesPhase[] }>('/sales/phases'),

  /**
   * 取引先検索（Autocomplete用）
   */
  searchAccounts: async (query: string, limit?: number): Promise<AccountSearchResult[]> => {
    const response = await api.get<{
      success: boolean;
      data: {
        accounts: AccountSearchResult[];
        hasMore: boolean;
      };
    }>('/sales/accounts/search', { params: { q: query, limit: limit || 50 } });
    return response.data.data.accounts;
  },

  /**
   * 取引先一覧取得
   */
  getAccounts: (params?: {
    search?: string;
    is_active?: boolean;
  }) =>
    api.get<{ success: boolean; data: Account[] }>('/sales/accounts', { params }),

  /**
   * 取引先新規登録
   */
  createAccount: (data: {
    account_name: string;
    account_name_kana?: string;
    industry?: string;
    notes?: string;
  }) =>
    api.post<{ success: boolean; data: { account_id: number } }>('/sales/accounts', data),

  /**
   * 取引先更新
   */
  updateAccount: (accountId: number, data: Partial<Account>) =>
    api.put<{ success: boolean; data: { message: string } }>(`/sales/accounts/${accountId}`, data),

  /**
   * 取引先削除（論理削除）
   */
  deleteAccount: (accountId: number) =>
    api.delete<{ success: boolean; data: { message: string } }>(`/sales/accounts/${accountId}`),

  /**
   * クローズ理由一覧取得
   */
  getCloseReasons: () =>
    api.get<{
      success: boolean;
      data: {
        all: CloseReason[];
        by_category: Record<string, Array<{ reason_code: string; reason_name: string }>>;
      };
    }>('/sales/close-reasons'),

  // ===============================
  // 発注先マスタAPI
  // ===============================

  /**
   * 発注先一覧取得
   */
  getVendors: (params?: {
    search?: string;
    is_active?: boolean;
  }) =>
    api.get<{ success: boolean; data: Vendor[] }>('/sales/vendors', { params }),

  /**
   * 発注先新規登録
   */
  createVendor: (data: {
    vendor_name: string;
    vendor_name_kana?: string;
    industry?: string;
    notes?: string;
  }) =>
    api.post<{ success: boolean; data: { vendor_id: number } }>('/sales/vendors', data),

  /**
   * 発注先更新
   */
  updateVendor: (vendorId: number, data: Partial<Vendor>) =>
    api.put<{ success: boolean; data: { message: string } }>(`/sales/vendors/${vendorId}`, data),

  /**
   * 発注先削除（論理削除）
   */
  deleteVendor: (vendorId: number) =>
    api.delete<{ success: boolean; data: { message: string } }>(`/sales/vendors/${vendorId}`),
};

// ===============================
// 外部システム連携API
// ===============================

export const salesSyncApi = {
  /**
   * 原価データ同期
   */
  syncCostData: (data: {
    case_ids?: number[];
    sync_type?: 'full' | 'partial' | 'cost_only';
    force_sync?: boolean;
  }) =>
    api.post<{
      success: boolean;
      data: {
        synced_count: number;
        failed_count: number;
        details: Array<{
          case_id: number;
          case_no: string;
          sync_status: string;
          error_message?: string;
        }>;
        sync_started_at: string;
        sync_completed_at: string;
      };
    }>('/sales/sync/cost-data', data),

  /**
   * 外部システム連携履歴取得
   */
  getSyncHistory: (params?: {
    case_id?: number;
    sync_status?: string;
    limit?: number;
  }) =>
    api.get<{ success: boolean; data: ExternalSyncHistory[] }>('/sales/sync/history', { params }),
};

// ===============================
// エクスポート・インポートAPI
// ===============================

export const salesExportApi = {
  /**
   * CSV出力
   */
  exportCsv: async (params?: Record<string, string | number | boolean | undefined>): Promise<Blob> => {
    const response = await api.get('/sales/export/csv', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Excel出力
   */
  exportExcel: async (params?: {
    year?: number;
  }): Promise<Blob> => {
    const response = await api.get('/sales/export/excel', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * CSVインポート
   */
  /**
   * Monday連携ZIP出力（cases/activities/recorded_rev の3CSV）
   */
  exportMonday: async (): Promise<Blob> => {
    const response = await api.get('/sales/export/monday', {
      responseType: 'blob',
    });
    return response.data;
  },

  importCsv: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<{
      success: boolean;
      data: {
        total_rows: number;
        imported_rows: number;
        failed_rows: number;
        errors: Array<{
          row: number;
          error: string;
        }>;
      };
    }>('/sales/import/csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// ===============================
// 営業権限管理API
// ===============================

export const salesPermissionsApi = {
  /**
   * 営業権限一覧取得
   */
  getPermissions: (params?: {
    department_id?: number;
  }) =>
    api.get<{
      success: boolean;
      data: Array<{
        permission_id: number;
        employee_id: string;
        employee_name: string;
        department_name: string;
        is_case_admin: boolean;
        is_master_admin: boolean;
        is_kpi_viewer: boolean;
        is_external_sync_admin: boolean;
      }>;
    }>('/sales/permissions', { params }),

  /**
   * 営業権限更新
   */
  updatePermission: (employeeId: string, data: {
    is_case_admin: boolean;
    is_master_admin: boolean;
    is_kpi_viewer: boolean;
    is_external_sync_admin: boolean;
  }) =>
    api.put<{ success: boolean; data: { message: string } }>(`/sales/permissions/${employeeId}`, data),

  /**
   * 営業権限新規登録
   */
  createPermission: (data: {
    employee_id: string;
    is_case_admin: boolean;
    is_master_admin: boolean;
    is_kpi_viewer: boolean;
    is_external_sync_admin: boolean;
  }) =>
    api.post<{ success: boolean; data: { permission_id: number } }>('/sales/permissions', data),

  /**
   * 営業権限削除
   */
  deletePermission: (employeeId: string) =>
    api.delete<{ success: boolean; data: { message: string } }>(`/sales/permissions/${employeeId}`),
};

// ===============================
// ===============================
// 計画スナップショットAPI
// ===============================

export interface PlanSnapshot {
  snapshotId: number;
  snapshotName: string;
  snapshotDate: string;
  fiscalYear: number;
  snapshotType: 'manual' | 'auto_weekly';
  description?: string;
  totalPlannedSales: number;
  totalPlannedCost: number;
  totalPlannedProfit: number;
  caseCount: number;
  createdAt: string;
  createdBy?: string;
}

export interface SnapshotCase {
  caseId: number;
  caseNo: string;
  caseName: string;
  customerName?: string;
  department?: string;
  ownerName?: string;
  phaseName?: string;
  status?: string;
  plannedSales: number;
  plannedCost: number;
  plannedProfit: number;
  monthlySales?: Record<string, number>;
  monthlyCost?: Record<string, number>;
}

export interface SnapshotComparisonBreakdownItem {
  count: number;
  salesImpact: number;
  costImpact: number;
  profitImpact: number;
}

export interface SnapshotComparisonBreakdown {
  newCases: SnapshotComparisonBreakdownItem;
  removedCases: SnapshotComparisonBreakdownItem;
  wonCases: SnapshotComparisonBreakdownItem;
  lostCases: SnapshotComparisonBreakdownItem;
  suspendedCases: SnapshotComparisonBreakdownItem;
  salesChanges: SnapshotComparisonBreakdownItem;
  costChanges: SnapshotComparisonBreakdownItem;
}

export interface SnapshotComparison {
  snapshot1: { name: string; date: string };
  snapshot2: { name: string; date: string };
  summary: {
    totalSales1: number;
    totalSales2: number;
    salesDiff: number;
    salesDiffPercent: number | string;
    totalCost1: number;
    totalCost2: number;
    costDiff: number;
    totalProfit1: number;
    totalProfit2: number;
    profitDiff: number;
    caseCount1: number;
    caseCount2: number;
    caseCountDiff: number;
    breakdown: SnapshotComparisonBreakdown;
    totalActivityCount: number;
    activities: Array<{
      activityId: number;
      caseId: number;
      caseNo: string;
      caseName: string;
      activityDate: string;
      activityType: string;
      description: string;
      createdByName: string;
    }>;
  };
  cases: Array<{
    caseId: number;
    caseNo: string;
    caseName: string;
    sales1: number;
    sales2: number;
    salesDiff: number;
    cost1: number;
    cost2: number;
    costDiff: number;
    profit1: number;
    profit2: number;
    profitDiff: number;
    changeReasons: string[];
    activityCount: number;
  }>;
}

export const salesSnapshotApi = {
  /**
   * スナップショット一覧取得
   */
  getSnapshots: (params?: {
    fiscalYear?: number;
    type?: 'manual' | 'auto_weekly';
  }) =>
    api.get<{
      success: boolean;
      data: PlanSnapshot[];
    }>('/sales/snapshots', { params }),

  /**
   * スナップショット詳細取得
   */
  getSnapshot: (snapshotId: number) =>
    api.get<{
      success: boolean;
      data: PlanSnapshot & { cases: SnapshotCase[] };
    }>(`/sales/snapshots/${snapshotId}`),

  /**
   * スナップショット作成（手動）
   */
  createSnapshot: (data: {
    snapshotName: string;
    description?: string;
  }) =>
    api.post<{
      success: boolean;
      data: PlanSnapshot;
      message: string;
    }>('/sales/snapshots', data),

  /**
   * スナップショット削除
   */
  deleteSnapshot: (snapshotId: number) =>
    api.delete<{
      success: boolean;
      message: string;
    }>(`/sales/snapshots/${snapshotId}`),

  /**
   * スナップショット比較
   * @param snapshot1 - 比較元（'current'で現在計画）
   * @param snapshot2 - 比較先（省略で現在計画）
   */
  compareSnapshots: (params: {
    snapshot1: number | 'current';
    snapshot2?: number | 'current';
    fiscalYear?: number;
  }) =>
    api.get<{
      success: boolean;
      data: SnapshotComparison;
    }>('/sales/snapshots/compare', { params }),
};

// ===============================
// 案件契約履歴API
// ===============================

export interface CaseContract {
  contract_id: number;
  case_id: number;
  contract_no?: string;
  contract_period_start: string;
  contract_period_end: string;
  contract_amount: number;
  contract_type: 'initial' | 'renewal' | 'expansion';
  renewal_status: 'pending' | 'confirmed' | 'lost';
  renewal_probability: number;
  notes?: string;
  created_by: string;
  created_by_name?: string;
  created_at: string;
  updated_at?: string;
}

export interface CaseMonthly {
  id: number;
  case_id: number;
  contract_id?: number;
  fiscal_year: number;
  month: number;
  planned_sales: number;
  planned_cost: number;
  planned_gross_profit: number;
  actual_sales: number;
  actual_cost: number;
  actual_gross_profit: number;
  sync_source?: string;
  synced_at?: string;
  created_at: string;
  updated_at?: string;
}

export interface CaseResource {
  id: number;
  case_id: number;
  employee_id: string;
  employee_name?: string;
  role?: string;
  fiscal_year: number;
  month: number;
  planned_hours: number;
  actual_hours: number;
  unit_price?: number;
  created_at: string;
  updated_at?: string;
}

export const salesCaseContractsApi = {
  /**
   * 契約履歴一覧取得
   */
  getContracts: (caseId: number) =>
    api.get<{
      success: boolean;
      data: CaseContract[];
    }>(`/sales/cases/${caseId}/contracts`),

  /**
   * 契約履歴登録
   */
  createContract: (caseId: number, data: Partial<CaseContract>) =>
    api.post<{
      success: boolean;
      data: CaseContract;
      message: string;
    }>(`/sales/cases/${caseId}/contracts`, data),

  /**
   * 契約履歴更新
   */
  updateContract: (caseId: number, contractId: number, data: Partial<CaseContract>) =>
    api.put<{
      success: boolean;
      data: CaseContract;
      message: string;
    }>(`/sales/cases/${caseId}/contracts/${contractId}`, data),

  /**
   * 契約履歴削除
   */
  deleteContract: (caseId: number, contractId: number) =>
    api.delete<{
      success: boolean;
      message: string;
    }>(`/sales/cases/${caseId}/contracts/${contractId}`),
};

export const salesCaseMonthlyApi = {
  /**
   * 月次予実取得
   */
  getMonthly: (caseId: number, fiscalYear?: number) =>
    api.get<{
      success: boolean;
      data: CaseMonthly[];
    }>(`/sales/cases/${caseId}/monthly`, {
      params: fiscalYear ? { fiscal_year: fiscalYear } : undefined
    }),

  /**
   * 月次予実一括更新
   */
  updateMonthly: (caseId: number, fiscalYear: number, monthlyData: Partial<CaseMonthly>[]) =>
    api.put<{
      success: boolean;
      data: CaseMonthly[];
      message: string;
    }>(`/sales/cases/${caseId}/monthly`, {
      fiscal_year: fiscalYear,
      monthly_data: monthlyData
    }),
};

export const salesCaseResourcesApi = {
  /**
   * 要員計画取得
   */
  getResources: (caseId: number, fiscalYear?: number) =>
    api.get<{
      success: boolean;
      data: CaseResource[];
    }>(`/sales/cases/${caseId}/resources`, {
      params: fiscalYear ? { fiscal_year: fiscalYear } : undefined
    }),

  /**
   * 要員計画一括更新
   */
  updateResources: (caseId: number, fiscalYear: number, resourceData: Partial<CaseResource>[]) =>
    api.put<{
      success: boolean;
      data: CaseResource[];
      message: string;
    }>(`/sales/cases/${caseId}/resources`, {
      fiscal_year: fiscalYear,
      resource_data: resourceData
    }),

  /**
   * 要員計画削除
   */
  deleteResource: (caseId: number, resourceId: number) =>
    api.delete<{
      success: boolean;
      message: string;
    }>(`/sales/cases/${caseId}/resources/${resourceId}`),
};

// ===============================
// 承認ワークフロー型定義
// ===============================

export interface PendingDataAllocation {
  employee_id?: string;
  resource_name?: string;
  resource_type?: string;
  vendor_id?: number;
  unit_price: number;
  cost_rate: number;
  start_month: string;
  end_month: string;
}

export interface PendingData {
  request_type: 'phase_change' | 'resource_change' | 'phase_and_amount_change' | 'initial';
  original_values: {
    case_name?: string;
    sales_phase_id?: number;
    sales_phase_name?: string;
    assigned_employee_id?: string;
    assigned_employee_name?: string;
    expected_order_amount?: number;
    gross_profit?: number;
    resource_count?: number;
  };
  new_values: {
    case_name?: string;
    case_summary?: string;
    account_id?: number;
    account_name?: string;
    service_department?: string;
    end_user?: string;
    business_category?: string;
    sales_phase_id?: number;
    sales_phase_name?: string;
    assigned_employee_id?: string;
    assigned_employee_name?: string;
    expected_order_amount?: number;
    gross_profit?: number;
    expected_order_date?: string;
    next_action_date?: string;
    next_action_memo?: string;
    external_project_id?: string;
    status?: string;
    close_result?: string;
    resource_count?: number;
  };
  original_allocations?: PendingDataAllocation[];
  resource_allocations?: PendingDataAllocation[];
  change_reason?: string;
}

export interface ApprovalRequest {
  case_id: number;
  case_no: string;
  case_name: string;
  status: string;
  approval_status: string;
  pending_data: PendingData;
  approver_id?: string;
  approver_name?: string;
  approval_requested_at?: string;
  approval_comment?: string;
  account_name?: string;
  requester_name?: string;
  requester_id?: string;
  current_phase_name?: string;
  current_amount?: number;
  current_gross_profit?: number;
  pending_amount?: number;
  pending_gross_profit?: number;
  pending_phase_name?: string;
}

export interface Approver {
  employee_id: string;
  name: string;
  department_name?: string;
}

// ===============================
// 承認ワークフローAPI
// ===============================

export const salesApprovalsApi = {
  /**
   * 承認待ち一覧取得（承認者向け）
   */
  getPendingApprovals: () =>
    api.get<{
      success: boolean;
      data: ApprovalRequest[];
    }>('/sales/approvals'),

  /**
   * 自分の申請一覧取得
   */
  getMyRequests: () =>
    api.get<{
      success: boolean;
      data: ApprovalRequest[];
    }>('/sales/approvals/my-requests'),

  /**
   * 承認依頼詳細取得
   */
  getApprovalDetail: (caseId: number) =>
    api.get<{
      success: boolean;
      data: ApprovalRequest & {
        current_amount: number;
        current_gross_profit: number;
        current_phase_code: string;
      };
    }>(`/sales/approvals/${caseId}`),

  /**
   * 承認依頼提出
   */
  submitApproval: (caseId: number, data: {
    approver_id: string;
    request_type: string;
    pending_data: object;
    change_reason?: string;
    is_new_case?: boolean;
  }) =>
    api.post<{
      success: boolean;
      message: string;
      data: { case_id: number };
    }>(`/sales/approvals/${caseId}/submit`, data),

  /**
   * 承認
   */
  approve: (caseId: number) =>
    api.put<{
      success: boolean;
      message: string;
      data: { case_id: number };
    }>(`/sales/approvals/${caseId}/approve`),

  /**
   * 却下
   */
  reject: (caseId: number, comment: string) =>
    api.put<{
      success: boolean;
      message: string;
      data: { case_id: number };
    }>(`/sales/approvals/${caseId}/reject`, { comment }),

  /**
   * 申請取消
   */
  cancel: (caseId: number) =>
    api.put<{
      success: boolean;
      message: string;
      data: { case_id: number };
    }>(`/sales/approvals/${caseId}/cancel`),

  /**
   * 未処理件数取得
   */
  getPendingCount: () =>
    api.get<{
      success: boolean;
      data: { count: number };
    }>('/sales/approvals/pending-count'),

  /**
   * 承認者候補一覧取得
   */
  getApprovers: () =>
    api.get<{
      success: boolean;
      data: Approver[];
    }>('/sales/approvals/approvers'),
};

export default {
  salesCasesApi,
  salesKpiApi,
  salesMasterApi,
  salesSyncApi,
  salesExportApi,
  salesPermissionsApi,
  salesSnapshotApi,
  salesCaseContractsApi,
  salesCaseMonthlyApi,
  salesCaseResourcesApi,
  salesApprovalsApi,
};
